import React, { useState, useEffect } from 'react';
import { Shield, Key, Plus, Trash2, Eye, EyeOff, RefreshCw, Activity } from 'lucide-react';
import './Admin.css';

const Admin = ({ isVisible, onClose }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [visibleKeys, setVisibleKeys] = useState(new Set());

  useEffect(() => {
    if (isVisible) {
      fetchApiKeys();
      fetchStats();
    }
  }, [isVisible]);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/keys');
      const data = await response.json();
      if (data.success) {
        setApiKeys(data.keys);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    
    try {
      const response = await fetch('/api/admin/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName })
      });
      
      const data = await response.json();
      if (data.success) {
        await fetchApiKeys();
        await fetchStats();
        setNewKeyName('');
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const toggleKeyStatus = async (keyId, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    try {
      const response = await fetch(`/api/admin/keys/${keyId}/${action}`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        await fetchApiKeys();
        await fetchStats();
      }
    } catch (error) {
      console.error(`Error ${action}ing API key:`, error);
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      const response = await fetch(`/api/admin/keys/${keyId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchApiKeys();
        await fetchStats();
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const toggleKeyVisibility = (keyId) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId);
    } else {
      newVisible.add(keyId);
    }
    setVisibleKeys(newVisible);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (!isVisible) return null;

  return (
    <div className="admin-overlay">
      <div className="admin-panel">
        <div className="admin-header">
          <div className="admin-title">
            <Shield size={24} />
            <h2>API Key Management</h2>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="admin-stats">
            <div className="stat-card">
              <Key size={20} />
              <div>
                <span className="stat-number">{stats.totalKeys}</span>
                <span className="stat-label">Total Keys</span>
              </div>
            </div>
            <div className="stat-card">
              <Activity size={20} />
              <div>
                <span className="stat-number">{stats.activeKeys}</span>
                <span className="stat-label">Active Keys</span>
              </div>
            </div>
            <div className="stat-card">
              <RefreshCw size={20} />
              <div>
                <span className="stat-number">{stats.recentlyUsed}</span>
                <span className="stat-label">Used (24h)</span>
              </div>
            </div>
          </div>
        )}

        <div className="admin-content">
          <div className="admin-actions">
            <button 
              className="refresh-button"
              onClick={() => { fetchApiKeys(); fetchStats(); }}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
              Refresh
            </button>
            
            <button 
              className="create-button"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              <Plus size={16} />
              Create New Key
            </button>
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <div className="create-form">
              <input
                type="text"
                placeholder="Key name (optional)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="key-name-input"
              />
              <div className="form-actions">
                <button onClick={createApiKey} className="confirm-button">
                  Create
                </button>
                <button 
                  onClick={() => setShowCreateForm(false)} 
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* API Keys List */}
          <div className="api-keys-list">
            {loading ? (
              <div className="loading">Loading API keys...</div>
            ) : apiKeys.length === 0 ? (
              <div className="no-keys">No API keys found</div>
            ) : (
              apiKeys.map((key) => (
                <div key={key.id} className={`api-key-item ${!key.isActive ? 'inactive' : ''}`}>
                  <div className="key-info">
                    <div className="key-header">
                      <h3>{key.name}</h3>
                      <span className={`status-badge ${key.isActive ? 'active' : 'inactive'}`}>
                        {key.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="key-details">
                      <div className="key-field">
                        <label>API Key:</label>
                        <div className="key-value">
                          <code>
                            {visibleKeys.has(key.id) 
                              ? key.plainKey 
                              : '••••••••••••••••••••••••••••••••'
                            }
                          </code>
                          <button 
                            onClick={() => toggleKeyVisibility(key.id)}
                            className="visibility-button"
                            title={visibleKeys.has(key.id) ? 'Hide' : 'Show'}
                          >
                            {visibleKeys.has(key.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          {visibleKeys.has(key.id) && (
                            <button 
                              onClick={() => copyToClipboard(key.plainKey)}
                              className="copy-button"
                              title="Copy to clipboard"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="key-metadata">
                        <span>Created: {formatDate(key.createdAt)}</span>
                        <span>Last Used: {formatDate(key.lastUsed)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="key-actions">
                    <button
                      onClick={() => toggleKeyStatus(key.id, key.isActive)}
                      className={`toggle-button ${key.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {key.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    
                    {key.id !== 'default' && (
                      <button
                        onClick={() => deleteApiKey(key.id)}
                        className="delete-button"
                        title="Delete API key"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-footer">
          <p className="warning">
            ⚠️ API keys are shown in plain text for development only. 
            In production, keys should never be displayed after creation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Admin;